import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@shared/api';
import { ModelLogo } from '@shared/brand';
import { ShareBar } from '@shared/charts';
import { compact, idr, num } from '@shared/format';
import { Alert, Card, Empty, Icon, Loading, PageHeader, Segmented, Select } from '../components/ui';
import {
  ActivityChart,
  BreakdownTable,
  PAGE_SIZE,
  Pager,
  RANGES,
  RequestDetailModal,
  RequestTable,
  STATUS_LABELS,
  UsageTiles,
  useGroupColors,
  type Activity,
  type GroupBy,
  type Metric,
  type Range,
  type UsageRow,
} from '../components/UsageBlocks';
import '../styles/billing.css';

const GROUPS: Array<{ id: GroupBy; label: string }> = [
  { id: 'model', label: 'Model' },
  { id: 'provider', label: 'Provider' },
  { id: 'api_key', label: 'API key' },
  { id: 'project', label: 'Proyek' },
];

interface ProjectOption {
  id: string;
  name: string;
  environment: string;
}

export function Usage() {
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useState<Range>('30d');
  const [groupBy, setGroupBy] = useState<GroupBy>('model');
  const [metric, setMetric] = useState<Metric>('spend_idr');
  const [activity, setActivity] = useState<Activity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const projectId = params.get('proyek') ?? '';

  const [rows, setRows] = useState<UsageRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [modelFilter, setModelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<UsageRow | null>(null);

  useEffect(() => {
    api<{ data: ProjectOption[] }>('/v1/projects')
      .then((body) => setProjects(body.data))
      .catch(() => setProjects([]));
  }, []);

  // one project, no "other"
  const effectiveGroup: GroupBy = projectId && groupBy === 'project' ? 'model' : groupBy;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const query = new URLSearchParams({ range, group_by: effectiveGroup });
    if (projectId) query.set('project_id', projectId);
    api<Activity>(`/v1/usage/activity?${query}`)
      .then((body) => alive && (setActivity(body), setError(null)))
      .catch((err: Error) => alive && setError(err.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [range, effectiveGroup, projectId]);

  useEffect(() => {
    if (!activity) return;
    let alive = true;
    const query = new URLSearchParams({ from: activity.from, to: activity.to, page: String(page), page_size: String(PAGE_SIZE) });
    if (modelFilter) query.set('model', modelFilter);
    if (statusFilter) query.set('status', statusFilter);
    if (projectId) query.set('project_id', projectId);
    api<{ data: UsageRow[]; total: number }>(`/v1/usage?${query}`)
      .then((body) => {
        if (!alive) return;
        setRows(body.data);
        setTotal(body.total);
      })
      .catch(() => alive && setRows([]));
    return () => {
      alive = false;
    };
  }, [activity, page, modelFilter, statusFilter, projectId]);

  const colorFor = useGroupColors(activity);

  const setProject = (id: string) => {
    const next = new URLSearchParams(params);
    if (id) next.set('proyek', id);
    else next.delete('proyek');
    setParams(next, { replace: true });
    setPage(1);
  };

  const modelOptions = useMemo(() => {
    const groups = activity && effectiveGroup === 'model' ? activity.groups.filter((g) => g.key !== 'other') : [];
    const options = [
      { value: '', label: 'Semua model', icon: <Icon name="grid" size={15} /> },
      ...groups.map((g) => ({
        value: g.key,
        label: g.label,
        text: `${g.label} ${g.key}`,
        icon: <ModelLogo model={g.key} family={g.family} provider={g.provider} size={20} />,
      })),
    ];
    if (modelFilter && !options.some((o) => o.value === modelFilter)) options.push({ value: modelFilter, label: modelFilter, icon: <Icon name="grid" size={15} /> });
    return options;
  }, [activity, effectiveGroup, modelFilter]);

  if (loading && !activity) return <Loading label="Memuat pemakaian" />;
  if (error && !activity) return <Alert kind="error">{error}</Alert>;
  if (!activity) return null;

  const t = activity.totals;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const periodLabel = RANGES.find((r) => r.id === range)?.label ?? '';
  const project = projects.find((p) => p.id === projectId);
  const groupOptions = projectId ? GROUPS.filter((g) => g.id !== 'project') : GROUPS;

  return (
    <>
      <PageHeader
        title="Pemakaian"
        subtitle={
          project
            ? `Biaya, token, dan request dari proyek ${project.name}. Isi prompt dan jawaban tidak pernah disimpan.`
            : 'Biaya, token, dan request dari semua key Anda. Isi prompt dan jawaban tidak pernah disimpan.'
        }
        actions={
          <>
            {projects.length > 0 && (
              <Select
                aria-label="Filter proyek"
                value={projectId}
                onChange={setProject}
                style={{ width: 'auto', minWidth: 190 }}
                options={[
                  { value: '', label: 'Semua proyek', icon: <Icon name="boxes" size={15} /> },
                  ...projects.map((p) => ({ value: p.id, label: p.name, icon: <Icon name="folder" size={15} /> })),
                ]}
              />
            )}
            <Segmented label="Rentang" options={RANGES} value={range} onChange={(v) => { setRange(v); setPage(1); }} />
            <Segmented label="Kelompokkan" options={groupOptions} value={effectiveGroup} onChange={setGroupBy} />
          </>
        }
      />

      {t.requests === 0 && !projectId && range === '30d' && (
        <Alert kind="info">
          Belum ada pemakaian dalam 30 hari terakhir. Coba model di <Link to="/playground">Playground</Link>, atau{' '}
          <Link to="/api-keys?baru=1">buat API key</Link> untuk aplikasi Anda. Statistik muncul di sini begitu ada request.
        </Alert>
      )}

      <UsageTiles activity={activity} periodLabel={periodLabel} />

      <ActivityChart activity={activity} metric={metric} onMetric={setMetric} colorFor={colorFor} periodLabel={periodLabel} loading={loading} />

      <div className="split mb">
        <Card
          title="Rincian"
          description={`Diurutkan dari biaya terbesar. ${activity.groups.length} ${GROUPS.find((g) => g.id === effectiveGroup)?.label.toLowerCase()} teratas.`}
        >
          <BreakdownTable
            activity={activity}
            by={effectiveGroup}
            label={GROUPS.find((g) => g.id === effectiveGroup)?.label ?? ''}
            colorFor={colorFor}
            onPick={effectiveGroup === 'model' ? (key) => { setModelFilter(key); setPage(1); } : undefined}
            pickTitle="Tampilkan log model ini"
          />
        </Card>

        <div className="grid" style={{ alignContent: 'start' }}>
          <Card title="Porsi biaya">
            <ShareBar
              items={activity.groups.map((g) => ({ key: g.key, label: g.label, value: g.totals.spend_idr }))}
              colorFor={(key) => colorFor(key)}
              formatValue={(v) => idr(v)}
            />
          </Card>
          <Card title="Komposisi token">
            <ShareBar
              items={[
                { key: 'in', label: 'Input', value: t.input_tokens },
                { key: 'cache', label: 'Cache', value: t.cached_tokens },
                { key: 'out', label: 'Output', value: t.output_tokens },
                { key: 'reason', label: 'Reasoning', value: t.reasoning_tokens },
              ]}
              formatValue={(v) => compact(v)}
            />
            <p className="field-hint mt-sm" style={{ marginBottom: 0 }}>
              Token dari cache ditagih lebih murah. Prompt panjang yang sering diulang paling untung dari cache.
            </p>
          </Card>
        </div>
      </div>

      <Card
        title="Log request"
        description={`${num(total)} request · klik baris untuk detail`}
        action={
          <div className="row wrap">
            <Select aria-label="Filter model" value={modelFilter} onChange={(v) => { setModelFilter(v); setPage(1); }} options={modelOptions} size="sm" style={{ width: 'auto', minWidth: 190 }} />
            <Select
              aria-label="Filter status"
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
              size="sm"
              style={{ width: 'auto', minWidth: 150 }}
              options={[{ value: '', label: 'Semua status' }, ...Object.entries(STATUS_LABELS).map(([id, label]) => ({ value: id, label: label ?? id }))]}
            />
            <Pager page={page} pages={pages} onPage={setPage} />
          </div>
        }
      >
        {rows.length === 0 ? (
          <Empty>{modelFilter || statusFilter ? 'Tidak ada request yang cocok dengan filter ini.' : 'Belum ada request di rentang ini.'}</Empty>
        ) : (
          <RequestTable rows={rows} onSelect={setSelected} />
        )}
      </Card>

      {selected && <RequestDetailModal row={selected} onClose={() => setSelected(null)} />}
    </>
  );
}
